import React from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../../components/PageShell';
import { SectionHeading } from '../../components/common/Primitives';
import { INSIGHTS } from '../../lib/mock2';
import { ArrowUpRight, PlayCircle, Clock, Calendar } from 'lucide-react';

function AuthorRow({ author, date, read }) {
  const initials = author.split(' ').map(s => s[0]).slice(0,2).join('');
  return (
    <div className="flex items-center gap-2 text-[11.5px] text-ink-500">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-logo text-[10px] font-bold text-white">{initials}</span>
      <span className="font-semibold text-navy">{author}</span>
      <span className="text-canvas-200">·</span>
      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {date}</span>
      <span className="text-canvas-200">·</span>
      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {read}</span>
    </div>
  );
}

function FeaturedCard({ post }) {
  return (
    <Link to={`/insights/${post.slug}`} className="group grid grid-cols-1 gap-6 overflow-hidden rounded-3xl border border-canvas-200 bg-white shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card md:grid-cols-[1.15fr_1fr]">
      <div className="relative aspect-[16/10] md:aspect-auto md:min-h-[340px]">
        <img src={post.img} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
        {post.hasVideo && (
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-white/95 text-navy shadow-soft"><PlayCircle className="h-8 w-8" /></span>
          </span>
        )}
        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[10.5px] font-bold tracking-widest text-purple">LATEST</span>
      </div>
      <div className="flex flex-col justify-between p-6 md:p-8">
        <div>
          <AuthorRow author={post.author} date={post.date} read={post.read} />
          <h3 className="mt-4 text-[24px] md:text-[28px] font-extrabold leading-snug text-navy group-hover:text-purple transition-colors">{post.title}</h3>
          <p className="mt-3 text-[14px] text-ink-700 leading-relaxed">{post.desc}</p>
        </div>
        <div className="mt-6 inline-flex items-center gap-1 text-[13px] font-semibold text-purple">Read the full article <ArrowUpRight className="h-4 w-4" /></div>
      </div>
    </Link>
  );
}

function PostCard({ post }) {
  return (
    <Link to={`/insights/${post.slug}`} className="group overflow-hidden rounded-2xl border border-canvas-200 bg-white shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card flex flex-col">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={post.img} alt={post.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        {post.hasVideo && (
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-white/95 text-navy shadow-soft"><PlayCircle className="h-6 w-6" /></span>
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <AuthorRow author={post.author} date={post.date} read={post.read} />
        <h4 className="mt-3 text-[16px] font-bold leading-snug text-navy group-hover:text-purple transition-colors">{post.title}</h4>
        <p className="mt-2 flex-1 text-[13px] leading-relaxed text-ink-500 line-clamp-4">{post.desc}</p>
        <div className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-purple">Read more <ArrowUpRight className="h-3.5 w-3.5" /></div>
      </div>
    </Link>
  );
}

export default function Insights() {
  const [featured, ...rest] = INSIGHTS;
  return (
    <PageShell>
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">Insights and updates</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">Research reflections, teacher workshops, product perspectives and deployment progress \u2014 from the Gyan Dhara team.</p>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="FEATURED" title="Latest from Gyan Dhara" desc="Recent articles authored by the Gyan Dhara team on education quality, research findings and teacher workshops." />
          <div className="mt-8"><FeaturedCard post={featured} /></div>
        </div>
      </section>

      <section className="px-6 py-8 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="ARCHIVE" title="All insights and updates" />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (<PostCard key={p.slug} post={p} />))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}

